import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

const emptyPartnership = () => ({
  badge_text: "",
  heading: "",
  heading_emphasis: "",
  paragraphs: [""],
  certs: [""],
  cta_text: "",
  cta_link: "",
  badge_accent_color: "#1C2C44",
  badge_label: "",
  badge_sublabel: "",
  badge_logo: "", // media picker value
});

// ─── Reusable Partnership Block ───────────────────────────────────────────────
function PartnershipBlock({ data, setData }) {
  const upd = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const updPara = (i, val) =>
    setData((d) => ({
      ...d,
      paragraphs: d.paragraphs.map((x, idx) => (idx === i ? val : x)),
    }));
  const addPara = () =>
    setData((d) => ({ ...d, paragraphs: [...(d.paragraphs || []), ""] }));
  const removePara = (i) =>
    setData((d) => ({
      ...d,
      paragraphs: d.paragraphs.filter((_, idx) => idx !== i),
    }));

  const updCert = (i, val) =>
    setData((d) => ({
      ...d,
      certs: d.certs.map((x, idx) => (idx === i ? val : x)),
    }));
  const addCert = () =>
    setData((d) => ({ ...d, certs: [...(d.certs || []), ""] }));
  const removeCert = (i) =>
    setData((d) => ({ ...d, certs: d.certs.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-3">
      <Field label="Badge Text">
        <TextInput
          value={data.badge_text || ""}
          onChange={(e) => upd("badge_text", e.target.value)}
        />
      </Field>
      <Field label="Heading">
        <TextInput
          value={data.heading || ""}
          onChange={(e) => upd("heading", e.target.value)}
        />
      </Field>
      <Field label="Heading Emphasis">
        <TextInput
          value={data.heading_emphasis || ""}
          onChange={(e) => upd("heading_emphasis", e.target.value)}
        />
      </Field>

      {/* Paragraphs */}
      <div className="flex justify-between mt-3 mb-1">
        <p className="text-xs font-semibold">Paragraphs</p>
        <button onClick={addPara} className="flex items-center gap-1 text-xs">
          <Plus size={12} /> Add
        </button>
      </div>
      {data.paragraphs?.map((p, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">
            <Field label={`Paragraph ${i + 1}`}>
              <TextArea
                value={p}
                onChange={(e) => updPara(i, e.target.value)}
              />
            </Field>
          </div>
          {data.paragraphs.length > 1 && (
            <button className="mt-6" onClick={() => removePara(i)}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}

      {/* Certifications */}
      <div className="flex justify-between mt-3 mb-1">
        <p className="text-xs font-semibold">Certifications</p>
        <button onClick={addCert} className="flex items-center gap-1 text-xs">
          <Plus size={12} /> Add
        </button>
      </div>
      {data.certs?.map((c, i) => (
        <div key={i} className="flex gap-2 items-center mb-2">
          <div className="flex-1">
            <TextInput
              value={c}
              placeholder={`Certification ${i + 1}`}
              onChange={(e) => updCert(i, e.target.value)}
            />
          </div>
          {data.certs.length > 1 && (
            <button onClick={() => removeCert(i)}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}

      {/* CTA */}
      <div className="grid grid-cols-2 gap-3 mt-2">
        <Field label="CTA Text">
          <TextInput
            value={data.cta_text || ""}
            onChange={(e) => upd("cta_text", e.target.value)}
          />
        </Field>
        <Field label="CTA Link">
          <TextInput
            value={data.cta_link || ""}
            onChange={(e) => upd("cta_link", e.target.value)}
          />
        </Field>
      </div>

      {/* Badge Card */}
      <p className="text-xs font-semibold text-muted-foreground mt-4 mb-2">
        Partner Badge Card
      </p>
      <Field label="Accent Color (hex)">
        <div className="flex gap-2 items-center">
          <TextInput
            value={data.badge_accent_color || ""}
            placeholder="#1C2C44"
            onChange={(e) => upd("badge_accent_color", e.target.value)}
          />
          {data.badge_accent_color && (
            <div
              className="w-8 h-8 rounded-lg border border-border shrink-0"
              style={{ backgroundColor: data.badge_accent_color }}
            />
          )}
        </div>
      </Field>
      <Field label="Badge Label">
        <TextInput
          value={data.badge_label || ""}
          onChange={(e) => upd("badge_label", e.target.value)}
        />
      </Field>
      <Field label="Badge Sub Label">
        <TextInput
          value={data.badge_sublabel || ""}
          onChange={(e) => upd("badge_sublabel", e.target.value)}
        />
      </Field>
      <Field label="Partner Logo">
        <MediaImageField
          label="Select Partner Logo"
          value={data.badge_logo || ""}
          onChange={(val) => upd("badge_logo", val)}
        />
      </Field>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function PartnersSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Technology Alliances");
  const [heroHeading, setHeroHeading] = useState("Our Technology Partners —");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState(
    "Best-in-Class Solutions, Certified Delivery",
  );
  const [heroDescription, setHeroDescription] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());

  /* ---------------- PARTNERSHIP CARDS (dynamic) ---------------- */
  const [partnerActive, setPartnerActive] = useState(true);
  const [partnerships, setPartnerships] = useState([emptyPartnership()]);

  const updatePartnership = (i, val) =>
    setPartnerships((s) => s.map((x, idx) => (idx === i ? val : x)));

  /* ---------------- ANALYTICS PARTNERS ---------------- */
  const [analyticsActive, setAnalyticsActive] = useState(true);
  const [analyticsBadgeText, setAnalyticsBadgeText] =
    useState("Extended Ecosystem");
  const [analyticsHeading, setAnalyticsHeading] = useState("Analytics &");
  const [analyticsHeadingEmphasis, setAnalyticsHeadingEmphasis] =
    useState("Platform Partners");
  const [analyticsDescription, setAnalyticsDescription] = useState("");
  const [analyticsColPartner, setAnalyticsColPartner] = useState("Partner");
  const [analyticsColDetails, setAnalyticsColDetails] = useState(
    "Partnership Details",
  );
  const [analyticsRows, setAnalyticsRows] = useState([
    { partner: "", details: "" },
  ]);

  /* ---------------- CERT BENEFITS ---------------- */
  const [certActive, setCertActive] = useState(true);
  const [certBadgeText, setCertBadgeText] = useState("Why It Matters");
  const [certHeading, setCertHeading] = useState(
    "What Our Certifications Mean",
  );
  const [certHeadingEmphasis, setCertHeadingEmphasis] =
    useState("for Your Project");
  const [certDescription, setCertDescription] = useState("");
  const [certCards, setCertCards] = useState([
    { icon: "", title: "", description: "" },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Work with Australia's Certified",
  );
  const [ctaHeadingEmphasis, setCtaHeadingEmphasis] = useState(
    "SAP & Salesforce Partner",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
    }

    // Support new array format AND old fixed-key format (backward compat)
    if (d.partnerships?.length) {
      setPartnerships(d.partnerships);
    } else {
      const legacy = [];
      if (d.sap_partnership) legacy.push(d.sap_partnership);
      if (d.salesforce_partnership) legacy.push(d.salesforce_partnership);
      if (d.boyum_partnership) legacy.push(d.boyum_partnership);
      if (legacy.length) setPartnerships(legacy);
    }

    if (d.analytics_partners) {
      setAnalyticsBadgeText(d.analytics_partners.badge_text || "");
      setAnalyticsHeading(d.analytics_partners.heading || "");
      setAnalyticsHeadingEmphasis(d.analytics_partners.heading_emphasis || "");
      setAnalyticsDescription(d.analytics_partners.description || "");
      setAnalyticsColPartner(d.analytics_partners.col_partner || "Partner");
      setAnalyticsColDetails(
        d.analytics_partners.col_details || "Partnership Details",
      );
      if (d.analytics_partners.rows?.length)
        setAnalyticsRows(d.analytics_partners.rows);
    }

    if (d.cert_benefits) {
      setCertBadgeText(d.cert_benefits.badge_text || "");
      setCertHeading(d.cert_benefits.heading || "");
      setCertHeadingEmphasis(d.cert_benefits.heading_emphasis || "");
      setCertDescription(d.cert_benefits.description || "");
      if (d.cert_benefits.cards?.length) setCertCards(d.cert_benefits.cards);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaHeadingEmphasis(d.final_cta.heading_emphasis || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
    },
    partnerships,
    analytics_partners: {
      badge_text: analyticsBadgeText,
      heading: analyticsHeading,
      heading_emphasis: analyticsHeadingEmphasis,
      description: analyticsDescription,
      col_partner: analyticsColPartner,
      col_details: analyticsColDetails,
      rows: analyticsRows,
    },
    cert_benefits: {
      badge_text: certBadgeText,
      heading: certHeading,
      heading_emphasis: certHeadingEmphasis,
      description: certDescription,
      cards: certCards,
    },
    final_cta: {
      heading: ctaHeading,
      heading_emphasis: ctaHeadingEmphasis,
      description: ctaDescription,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroPrimary,
    heroSecondary,
    partnerships,
    analyticsBadgeText,
    analyticsHeading,
    analyticsHeadingEmphasis,
    analyticsDescription,
    analyticsColPartner,
    analyticsColDetails,
    analyticsRows,
    certBadgeText,
    certHeading,
    certHeadingEmphasis,
    certDescription,
    certCards,
    ctaHeading,
    ctaHeadingEmphasis,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />
      </CollapsibleSection>

      {/* DYNAMIC PARTNERSHIP CARDS */}
      <CollapsibleSection
        title="Partnership Sections"
        active={partnerActive}
        onToggleActive={setPartnerActive}
      >
        <div className="flex justify-between mb-4">
          <p className="text-xs text-muted-foreground">
            Each partnership renders as a full alternating section on the page.
          </p>
          <button
            onClick={() => setPartnerships((s) => [...s, emptyPartnership()])}
            className="flex items-center gap-1 text-xs shrink-0"
          >
            <Plus size={14} /> Add Partnership
          </button>
        </div>

        {partnerships.map((p, i) => (
          <div key={i} className="border rounded-xl mb-4 overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-4 py-3 bg-muted border-b border-border">
              <p className="text-xs font-semibold">
                {p.badge_text || p.heading || `Partnership ${i + 1}`}
              </p>
              <div className="flex gap-2 items-center">
                <ReorderButtons
                  index={i}
                  total={partnerships.length}
                  onMove={(f, t) =>
                    setPartnerships(moveItem(partnerships, f, t))
                  }
                />
                {partnerships.length > 1 && (
                  <button
                    onClick={() =>
                      setPartnerships((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} className="text-destructive" />
                  </button>
                )}
              </div>
            </div>
            {/* Card body */}
            <div className="p-4">
              <PartnershipBlock
                data={p}
                setData={(val) =>
                  updatePartnership(i, typeof val === "function" ? val(p) : val)
                }
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* ANALYTICS PARTNERS */}
      <CollapsibleSection
        title="Analytics & Platform Partners"
        active={analyticsActive}
        onToggleActive={setAnalyticsActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={analyticsBadgeText}
            onChange={(e) => setAnalyticsBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={analyticsHeading}
            onChange={(e) => setAnalyticsHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={analyticsHeadingEmphasis}
            onChange={(e) => setAnalyticsHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={analyticsDescription}
            onChange={(e) => setAnalyticsDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Col 1 Header">
            <TextInput
              value={analyticsColPartner}
              onChange={(e) => setAnalyticsColPartner(e.target.value)}
            />
          </Field>
          <Field label="Col 2 Header">
            <TextInput
              value={analyticsColDetails}
              onChange={(e) => setAnalyticsColDetails(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Partner Rows</p>
          <button
            onClick={() =>
              setAnalyticsRows((s) => [...s, { partner: "", details: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Row
          </button>
        </div>
        {analyticsRows.map((row, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Row {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={analyticsRows.length}
                  onMove={(f, t) =>
                    setAnalyticsRows(moveItem(analyticsRows, f, t))
                  }
                />
                {analyticsRows.length > 1 && (
                  <button
                    onClick={() =>
                      setAnalyticsRows((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Partner Name">
              <TextInput
                value={row.partner}
                onChange={(e) =>
                  setAnalyticsRows((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, partner: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Partnership Details">
              <TextArea
                value={row.details}
                onChange={(e) =>
                  setAnalyticsRows((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, details: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* CERT BENEFITS */}
      <CollapsibleSection
        title="Why Certification Matters"
        active={certActive}
        onToggleActive={setCertActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={certBadgeText}
            onChange={(e) => setCertBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={certHeading}
            onChange={(e) => setCertHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={certHeadingEmphasis}
            onChange={(e) => setCertHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={certDescription}
            onChange={(e) => setCertDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Benefit Cards</p>
          <button
            onClick={() =>
              setCertCards((s) => [
                ...s,
                { icon: "", title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {certCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={certCards.length}
                  onMove={(f, t) => setCertCards(moveItem(certCards, f, t))}
                />
                {certCards.length > 1 && (
                  <button
                    onClick={() =>
                      setCertCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setCertCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setCertCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setCertCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={ctaHeadingEmphasis}
            onChange={(e) => setCtaHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
